import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_compliance-pro-24/artifacts/c65ii9su_WINTRUST%20LOGO.png";
const FACEBOOK_URL = "https://www.facebook.com/share/1KXgzbQ4zP/";
const INSTAGRAM_URL = "https://www.instagram.com/wintrustsolutions";

const CONTACT = {
  phone: "+91 89409 88776",
  email: "wintrustsolutions@gmail.com",
  address: "Coimbatore, Tamil Nadu",
};

const Footer = () => {
  const services = [
    { label: "GST Registration", href: "/gst-registration" },
    { label: "GST Return Filing", href: "/gst-return-filing" },
    { label: "E-Commerce GST Registration", href: "/ecom-gst-registration" },
    { label: "E-Commerce GST Filing", href: "/ecom-gst-filing" },
    { label: "ITR Filing", href: "/itr-filing" },
    { label: "TDS Filing", href: "/tds-filing" },
    { label: "Business Registration", href: "/business-registration" },
    { label: "Health Insurance Advisory", href: "/health-insurance-advisory" },
    { label: "Business Loan Assistance", href: "/business-loan-assistance" },
    { label: "Personal Loan Assistance", href: "/personal-loan-assistance" },
  ];

  const tools = [
    { label: "Compliance Roadmap", href: "/compliance-roadmap" },
    { label: "EMI Calculator", href: "/emi-calculator" },
    { label: "GST Penalty Calculator", href: "/gst-penalty-calculator" },
    { label: "GST Interest Calculator", href: "/gst-interest-calculator" },
    { label: "Income Tax Calculator", href: "/income-tax-calculator" },
    { label: "TDS Penalty Calculator", href: "/tds-penalty-calculator" },
    { label: "Loan Eligibility Calculator", href: "/loan-eligibility-calculator" },
    { label: "ITR Checklist", href: "/itr-checklist" },
    { label: "GST Notice Template", href: "/gst-notice-response-template" },
    { label: "GST Reconciliation Worksheet", href: "/gst-reconciliation-worksheet" },
  ];

  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "Blog", href: "https://wintrustsolutions.blogspot.com/", external: true },
    { label: "Contact", href: "/#contact" },
  ];

  const legal = [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
  ];

  const serviceAreas = ["Coimbatore", "Tirupur", "Erode", "Salem", "Pollachi"];

  return (
    <footer className="bg-navy-900 text-white pt-16 pb-8" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          {/* Logo & About */}
          <div className="lg:col-span-2">
            <img
              src={LOGO_URL}
              alt="WINTRUST SOLUTIONS"
              className="h-16 w-auto mb-4 rounded bg-white p-1"
            />
            <p className="text-slate-400 text-sm mb-4">
              <strong className="text-white">WINTRUST SOLUTIONS</strong> - Your trusted tax consultant in Coimbatore. Professional GST, ITR, TDS, and compliance services for businesses and individuals.
            </p>
            <div className="space-y-2 text-slate-400 text-sm">
              <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2 hover:text-gold">
                <Phone size={16} /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-gold">
                <Mail size={16} /> {CONTACT.email}
              </a>
              <p className="flex items-center gap-2">
                <MapPin size={16} /> {CONTACT.address}
              </p>
            </div>
            <div className="flex gap-3 mt-5">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:bg-gold hover:border-gold hover:text-white transition-all"
                aria-label="Follow us on Facebook"
                data-testid="footer-facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:bg-gold hover:border-gold hover:text-white transition-all"
                aria-label="Follow us on Instagram"
                data-testid="footer-instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div data-testid="footer-services">
            <h4 className="text-gold font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              {services.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-slate-400 hover:text-gold text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Tools & Resources */}
          <div data-testid="footer-tools">
            <h4 className="text-gold font-semibold mb-4">Tools & Resources</h4>
            <ul className="space-y-2">
              {tools.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-slate-400 hover:text-gold text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links & Legal */}
          <div>
            <h4 className="text-gold font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 mb-6">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  {link.external ? (
                    <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-gold text-sm">
                      {link.label}
                    </a>
                  ) : link.href.startsWith("/#") ? (
                    <a href={link.href} className="text-slate-400 hover:text-gold text-sm">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="text-slate-400 hover:text-gold text-sm">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            <h4 className="text-gold font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              {legal.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-slate-400 hover:text-gold text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="text-gold font-semibold mb-4">Service Areas</h4>
            <ul className="space-y-2">
              {serviceAreas.map((area) => (
                <li key={area} className="text-slate-400 text-sm">
                  {area}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} WINTRUST SOLUTIONS. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy-policy" className="text-slate-400 hover:text-gold">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-slate-400 hover:text-gold">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
